import { Space } from "antd";
import { get, isNil } from "lodash";
import PropTypes from "prop-types";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import Switch from "shared/components/Switch";
import {
  DATE_BOUND_UNIT,
  FIELD_VALIDATION_LIMITS,
} from "utils/validation-rules";
import { DateBoundEditor, NumberField } from "./dateBoundControls";

const WEEKDAY_VALUES = [0, 1, 2, 3, 4, 5, 6];

const isNumericInputType = (type) => type === "number" || type === "slider";

const PICKER_OPTIONS = ["date", "week", "month", "year", "quarter"];

const FORMAT_OPTIONS = [
  "DD/MM/YYYY",
  "YYYY-MM-DD",
  "DD/MM/YY",
  "MM/YYYY",
  "YYYY",
];

const cleanProps = (props, keys) => {
  const next = {};
  keys.forEach((key) => {
    const value = props[key];
    if (isNil(value) || value === "") return;
    next[key] = value;
  });
  return Object.keys(next).length ? next : undefined;
};

const omitEmpty = (object) => {
  const next = {};
  Object.entries(object || {}).forEach(([key, value]) => {
    if (isNil(value) || value === "") return;
    if (value === false) return;
    if (Array.isArray(value) && !value.length) return;
    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      !Object.keys(value).length
    ) {
      return;
    }
    next[key] = value;
  });
  return next;
};

const DateConfig = ({ config, props, labels, patch, patchProps }) => (
  <>
    <DateBoundEditor
      label={labels.MIN_DATE}
      value={config.minDate}
      labels={labels}
      defaultSign="-"
      defaultAmount={FIELD_VALIDATION_LIMITS.BIRTH_DATE_MIN_YEARS}
      defaultUnit={DATE_BOUND_UNIT.YEAR}
      onChange={(minDate) => patch({ minDate })}
    />
    <DateBoundEditor
      label={labels.MAX_DATE}
      value={config.maxDate}
      labels={labels}
      defaultSign="-"
      defaultAmount={FIELD_VALIDATION_LIMITS.ADULT_MIN_AGE}
      defaultUnit={DATE_BOUND_UNIT.YEAR}
      onChange={(maxDate) => patch({ maxDate })}
    />
    <div className="canvas-editor__required-row">
      <Switch
        checked={!!config.disablePast}
        onChange={(checked) => patch({ disablePast: checked || undefined })}
      />
      <span className="canvas-editor__label">{labels.DISABLE_PAST}</span>
    </div>
    <div className="canvas-editor__required-row">
      <Switch
        checked={!!config.disableFuture}
        onChange={(checked) => patch({ disableFuture: checked || undefined })}
      />
      <span className="canvas-editor__label">{labels.DISABLE_FUTURE}</span>
    </div>
    <div className="canvas-editor__required-row">
      <Switch
        checked={!!config.usePlanDeltas}
        onChange={(checked) => patch({ usePlanDeltas: checked })}
      />
      <span className="canvas-editor__label">{labels.USE_PLAN_DELTAS}</span>
    </div>
    <Select
      mode="multiple"
      label={labels.DISABLED_WEEKDAYS}
      placeholder={labels.DISABLED_WEEKDAYS}
      options={WEEKDAY_VALUES.map((day) => ({
        value: day,
        label: get(labels, ["WEEKDAYS", day], String(day)),
      }))}
      value={config.disabledWeekdays || []}
      onChange={(next) =>
        patch({
          disabledWeekdays: next && next.length ? next : undefined,
        })
      }
    />
    <Select
      allowClear
      label={labels.PICKER}
      placeholder={labels.PICKER}
      options={PICKER_OPTIONS.map((picker) => ({
        value: picker,
        label: get(labels, ["PICKER_OPTIONS", picker], picker),
      }))}
      value={props.picker || undefined}
      onChange={(picker) => patchProps({ picker: picker || undefined })}
    />
    <Select
      allowClear
      showSearch
      label={labels.FORMAT}
      placeholder={labels.FORMAT}
      options={FORMAT_OPTIONS.map((format) => ({
        value: format,
        label: format,
      }))}
      value={props.format || undefined}
      onChange={(format) => patchProps({ format: format || undefined })}
    />
    <div className="canvas-editor__required-row">
      <Switch
        checked={!!props.showTime}
        onChange={(checked) => patchProps({ showTime: checked || undefined })}
      />
      <span className="canvas-editor__label">{labels.SHOW_TIME}</span>
    </div>
    <div className="canvas-editor__required-row">
      <Switch
        checked={props.allowClear !== false}
        onChange={(checked) =>
          patchProps({ allowClear: checked ? undefined : false })
        }
      />
      <span className="canvas-editor__label">{labels.ALLOW_CLEAR}</span>
    </div>
  </>
);

const TextConfig = ({ props, labels, patchProps }) => (
  <>
    <NumberField
      label={labels.MIN_LENGTH}
      value={props.minLength}
      placeholder={String(FIELD_VALIDATION_LIMITS.NAME_MIN_LENGTH)}
      onChange={(minLength) => patchProps({ minLength })}
    />
    <NumberField
      label={labels.MAX_LENGTH}
      value={props.maxLength}
      placeholder={String(FIELD_VALIDATION_LIMITS.NAME_MAX_LENGTH)}
      onChange={(maxLength) => patchProps({ maxLength })}
    />
    <Input
      label={labels.PLACEHOLDER}
      placeholder={labels.PLACEHOLDER}
      value={props.placeholder || ""}
      onChange={(event) =>
        patchProps({ placeholder: event.target.value || undefined })
      }
    />
  </>
);

const NumberConfig = ({ props, labels, patchProps }) => (
  <>
    <NumberField
      label={labels.MIN}
      value={props.min}
      placeholder={String(FIELD_VALIDATION_LIMITS.POSITIVE_NUMBER_MIN)}
      onChange={(min) => patchProps({ min })}
    />
    <NumberField
      label={labels.MAX}
      value={props.max}
      placeholder={String(FIELD_VALIDATION_LIMITS.NUMBER_MAX_PLACEHOLDER)}
      onChange={(max) => patchProps({ max })}
    />
    <NumberField
      label={labels.STEP}
      value={props.step}
      placeholder={String(FIELD_VALIDATION_LIMITS.POSITIVE_NUMBER_STEP)}
      step="any"
      onChange={(step) => patchProps({ step })}
    />
    <Input
      label={labels.PLACEHOLDER}
      placeholder={labels.PLACEHOLDER}
      value={props.placeholder || ""}
      onChange={(event) =>
        patchProps({ placeholder: event.target.value || undefined })
      }
    />
  </>
);

const PhoneEmailConfig = ({ props, labels, patchProps }) => (
  <>
    <NumberField
      label={labels.MAX_LENGTH}
      value={props.maxLength}
      placeholder={labels.MAX_LENGTH}
      onChange={(maxLength) => patchProps({ maxLength })}
    />
    <Input
      label={labels.PLACEHOLDER}
      placeholder={labels.PLACEHOLDER}
      value={props.placeholder || ""}
      onChange={(event) =>
        patchProps({ placeholder: event.target.value || undefined })
      }
    />
  </>
);

const FieldInputConfigEditor = ({
  value = {},
  labels = {},
  inputType = "auto",
  onChange,
}) => {
  const type = inputType || "auto";
  const config = value || {};
  const props = config.props || {};

  const emit = (nextConfig) => {
    const nextProps = nextConfig.props || {};
    let cleaned;
    if (type === "date") {
      cleaned = omitEmpty({
        minDate: nextConfig.minDate,
        maxDate: nextConfig.maxDate,
        disablePast: nextConfig.disablePast,
        disableFuture: nextConfig.disableFuture,
        disabledWeekdays: nextConfig.disabledWeekdays,
        props: cleanProps(nextProps, [
          "picker",
          "format",
          "showTime",
          "allowClear",
          "placeholder",
        ]),
      });
      if (nextConfig.usePlanDeltas === true) {
        cleaned.usePlanDeltas = true;
      } else if (nextConfig.usePlanDeltas === false) {
        cleaned.usePlanDeltas = false;
      }
    } else if (isNumericInputType(type)) {
      cleaned = omitEmpty({
        props: cleanProps(nextProps, ["min", "max", "step", "placeholder"]),
      });
    } else if (type === "email" || type === "phone") {
      cleaned = omitEmpty({
        props: cleanProps(nextProps, ["maxLength", "placeholder"]),
      });
    } else if (type === "text") {
      cleaned = omitEmpty({
        props: cleanProps(nextProps, [
          "minLength",
          "maxLength",
          "placeholder",
          "rows",
        ]),
      });
    } else {
      cleaned = {};
    }
    onChange(cleaned);
  };

  const patch = (partial) => emit({ ...config, ...partial });
  const patchProps = (partial) =>
    emit({
      ...config,
      props: { ...props, ...partial },
    });

  if (type === "auto") {
    return (
      <p className="canvas-editor__meta">{labels.SELECT_TYPE_FOR_CONFIG}</p>
    );
  }

  return (
    <Space direction="vertical" size={10} className="canvas-editor__full-width">
      {type === "date" ? (
        <DateConfig
          config={config}
          props={props}
          labels={labels}
          patch={patch}
          patchProps={patchProps}
        />
      ) : null}
      {type === "text" ? (
        <TextConfig props={props} labels={labels} patchProps={patchProps} />
      ) : null}
      {isNumericInputType(type) ? (
        <NumberConfig props={props} labels={labels} patchProps={patchProps} />
      ) : null}
      {type === "email" || type === "phone" ? (
        <PhoneEmailConfig
          props={props}
          labels={labels}
          patchProps={patchProps}
        />
      ) : null}
    </Space>
  );
};

FieldInputConfigEditor.propTypes = {
  value: PropTypes.object,
  labels: PropTypes.object,
  inputType: PropTypes.string,
  onChange: PropTypes.func,
};

FieldInputConfigEditor.defaultProps = {
  value: {},
  labels: {},
  inputType: "auto",
  onChange: undefined,
};

export default FieldInputConfigEditor;
